import React from 'react';

import type {
  CaraWorkspaceFileInput,
  CustomFormFieldsFragment,
  CustomFormListFieldsFragment
} from '~/api/graphql';
import {
  visibilityValueFromFragment,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';

export type CustomFormEditState = {
  form: CustomFormListFieldsFragment | CustomFormFieldsFragment;
  title: string;
  fillPrompt: string;
  ingestionGuidance: string;
  replacementFiles: CaraWorkspaceFileInput[];
  visibility: VisibilitySelectorValue;
};

export type CustomFormEditStatePatch = Partial<Omit<CustomFormEditState, 'form'>>;

/**
 * Holds the editable state for the custom-form edit dialog. The dialog
 * itself is "dumb" — it renders inputs and delegates patches up to this
 * hook so siblings (e.g. the save action) can read the same state.
 * Submission status (saving) lives outside this hook because it is driven
 * by the router's navigation state, not local edits.
 */
export function useCustomFormEditState(
  form: CustomFormListFieldsFragment | CustomFormFieldsFragment
) {
  const [state, setState] = React.useState<CustomFormEditState>(() => ({
    form,
    title: form.title,
    fillPrompt: form.fillPrompt,
    ingestionGuidance: form.ingestionGuidance,
    replacementFiles: [],
    visibility: visibilityValueFromFragment(form.visibility)
  }));

  // Reset the editable state when the dialog is opened on a *different* form
  // (different `id`). Edits on the same form are intentionally preserved across
  // re-renders, including those triggered by parent refetches that may hand us
  // a new fragment object with the same id.
  const lastFormIdRef = React.useRef(form.id);
  React.useEffect(() => {
    if (lastFormIdRef.current === form.id) return;
    lastFormIdRef.current = form.id;
    setState({
      form,
      title: form.title,
      fillPrompt: form.fillPrompt,
      ingestionGuidance: form.ingestionGuidance,
      replacementFiles: [],
      visibility: visibilityValueFromFragment(form.visibility)
    });
  }, [form]);

  const patch = React.useCallback((p: CustomFormEditStatePatch) => {
    setState((prev) => ({ ...prev, ...p }));
  }, []);

  return { state, patch };
}
